/* eslint-disable id-length, perfectionist/sort-objects */
import { clamp } from '@deltasierra/utilities-math';
import {
    ColorScheme,
    ColorSchemeHelper,
    RGB,
    parseDegree,
    parseOpacity,
    parsePercent,
    RGBHelper,
    sanitiseDegree,
    sanitiseOpacity,
    sanitisePercent,
} from './common';

/**
 * Represents a color in HSL color space.
 * Hue is expressed as an integer between 0 and 360.
 * Saturation is express as a decimal between 0 and 1.
 * Brightness (v for Value) is expressed as a decimal between 0 and 1.
 * The Brightness value range is like a black gradient overlay that
 * varies between 0 (black and fully opaque) to 1 (black and fully transparent)
 */
export interface HSV {
    h: number;
    s: number;
    v: number;
    a?: number;
}

/*
 * These offsets are used when converting HSV to RGB. They are constants based
 * On this formula: https://en.wikipedia.org/wiki/HSL_and_HSV#Alternative_HSV_conversion
 */
enum RgbConversionOffset {
    R = 5,
    G = 3,
    B = 1,
}

export class HSVHelper implements ColorSchemeHelper<HSV> {
    public scheme: ColorScheme = 'hsv';

    private rgbHelper = new RGBHelper();

    public isMatch(color: object): color is HSV {
        return 'h' in color && 's' in color && 'v' in color;
    }

    public sanitise(color: HSV): HSV {
        return {
            h: sanitiseDegree(color.h),
            s: sanitisePercent(color.s),
            v: sanitisePercent(color.v),
            a: sanitiseOpacity(color.a),
        };
    }

    public parse(color: string): HSV | undefined {
        const patterns = [
            /^(?:hsv|hsb)\(\s*((?:\d+(?:\.\d+)?)|\.\d+)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\)$/i,

            /^(?:hsva|hsba)\(\s*((?:\d+(?:\.\d+)?)|\.\d+)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%)(?:\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+))\s*)\)$/i,
        ];

        const match = patterns.map(pattern => pattern.exec(color)).filter(m => !!m)[0];

        if (!match) {
            return undefined;
        }

        const result = this.sanitise({
            h: parseDegree(match[1]),
            s: parsePercent(match[2]),
            v: parsePercent(match[3]),
            a: parseOpacity(match[4]),
        });

        return result;
    }

    public toString(color: HSV): string {
        // eslint-disable-next-line prefer-const
        let { a, h, s, v } = color;

        h = clamp(0, 360, Math.round(h));
        s = clamp(0, 100, Math.round(s * 100));
        v = clamp(0, 100, Math.round(v * 100));

        if (a === 1 || a === undefined) {
            return `hsv(${h}, ${s}%, ${v}%)`;
        }

        return `hsva(${h}, ${s}%, ${v}%, ${a})`;
    }

    public fromRgb(rgb: RGB): HSV {
        const { a, b, g, r } = rgb;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const d = max - min;
        let h = 0;
        const s = max === 0 ? 0 : d / max;
        const v = max / 255;

        switch (max) {
            case min:
                h = 0;
                break;
            case r:
                h = g - b + d * (g < b ? 6 : 0);
                h /= 6 * d;
                break;
            case g:
                h = b - r + d * 2;
                h /= 6 * d;
                break;
            case b:
                h = r - g + d * 4;
                h /= 6 * d;
                break;
            default:
                break;
        }

        const result = this.sanitise({ h: h * 360, s, v, a });

        return result;
    }

    public toRgb(hsv: HSV, options?: { round?: boolean }): RGB {
        const offsets = [RgbConversionOffset.R, RgbConversionOffset.G, RgbConversionOffset.B];
        const [r, g, b] = offsets.map(offset => this.calculateRgbValueBasedOnOffset(offset, hsv));
        const round = options && options.round ? options.round : true;
        return this.rgbHelper.sanitise({ r, g, b, a: hsv.a }, round);
    }

    public areEqual(colorA: HSV, colorB: HSV): boolean {
        const aIsNull = colorA === null;

        const bIsNull = colorB === null;

        if (aIsNull || bIsNull) {
            return aIsNull === bIsNull;
        }

        const aIsOpaque = colorA.a === 1 || colorA.a === undefined;
        const bIsOpaque = colorB.a === 1 || colorB.a === undefined;

        const areEqual =
            colorA.h === colorB.h &&
            colorA.s === colorB.s &&
            colorA.v === colorB.v &&
            (colorA.a === colorB.a || aIsOpaque === bIsOpaque);

        return areEqual;
    }

    /**
     * Calculates the R, G, or B value for the given HSV. The output is based on which offset is provided.
     *
     * @see https://en.wikipedia.org/wiki/HSL_and_HSV#Alternative_HSV_conversion
     * @param offset - Offset
     * @param hsv - HSV
     * @returns The offset
     */
    private calculateRgbValueBasedOnOffset(offset: RgbConversionOffset, hsv: HSV): number {
        const { h, s, v } = hsv;
        const k = (offset + h / 60) % 6;
        const rgbPercentage = v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
        return rgbPercentage * 255;
    }
}
